import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './features/auth/AuthProvider';
import { canMarkAttendance } from '@/lib/permissions';
import { BranchProvider } from './contexts/BranchContext';
import { ActiveChildProvider } from './contexts/ActiveChildContext';
import { ToastProvider } from './components/providers/ToastProvider';
import { ProtectedLayout } from './components/layout/ProtectedLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import { RoleGuard } from './components/auth/RoleGuard';
import { FeedbackHistoryGuard } from './components/auth/FeedbackHistoryGuard';
import { LoadingScreen } from './components/loading/LoadingScreen';
import { RolesProvider } from './contexts/RolesContext';
import { GuidedTourProvider } from './components/tour/GuidedTourProvider';

// Lazy load auth components
const Login = lazy(() => import('./features/auth/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./features/auth/Register').then(m => ({ default: m.Register })));
const ParentRegister = lazy(() => import('./features/auth/ParentRegister').then(m => ({ default: m.ParentRegister })));
const OnboardingWizard = lazy(() => import('./features/onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })));

// CODE SPLITTING: Lazy load all routes for optimal performance
// Dashboard and core features
const Dashboard = lazy(() => import('./features/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const ClassSchedule = lazy(() => import('./features/schedule/ClassSchedule').then(m => ({ default: m.ClassSchedule })));
const AttendancePage = lazy(() => import('./features/attendance/AttendancePage').then(m => ({ default: m.AttendancePage })));
const FinancePage = lazy(() => import('./features/finance/FinancePage').then(m => ({ default: m.FinancePage })));

// Social features
const MessagesPage = lazy(() => import('./features/messaging/MessagesPage').then(m => ({ default: m.MessagesPage })));
const FeedPage = lazy(() => import('./features/feed/FeedPage').then(m => ({ default: m.FeedPage })));
const NotificationsPage = lazy(() => import('./features/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const PointsHistoryPage = lazy(() => import('./features/wallet/PointsHistoryPage').then(m => ({ default: m.PointsHistoryPage })));

// Profile and gamification
const LeaderboardPage = lazy(() => import('./features/gamification/LeaderboardPage').then(m => ({ default: m.LeaderboardPage })));
const ProfilePage = lazy(() => import('./features/profile/ProfilePage').then(m => ({ default: m.ProfilePage })));
const PublicProfile = lazy(() => import('./features/profile/PublicProfile').then(m => ({ default: m.PublicProfile })));

// Wallet and subscriptions
const WalletPage = lazy(() => import('./features/wallet/WalletPage').then(m => ({ default: m.WalletPage })));
const FamilyWalletPage = lazy(() => import('./features/wallet/FamilyWalletPage').then(m => ({ default: m.FamilyWalletPage })));
const MySubscriptionPage = lazy(() => import('./features/subscription/MySubscriptionPage').then(m => ({ default: m.MySubscriptionPage })));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SubscriptionsPage = lazy(() => import('./features/subscriptions/SubscriptionsPage').then(m => ({ default: m.SubscriptionsPage })));

// Admin features
const UserManagement = lazy(() => import('./features/admin/UserManagement').then(m => ({ default: m.UserManagement })));
const MemberProfilePage = lazy(() => import('./features/admin/MemberProfilePage').then(m => ({ default: m.MemberProfilePage })));
const PerformanceDashboard = lazy(() => import('./features/admin/PerformanceDashboard').then(m => ({ default: m.PerformanceDashboard })));
const AnalyticsDashboard = lazy(() => import('./features/analytics/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const AuditLogPage = lazy(() => import('./features/admin/audit/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const BulkWalletAdjustPage = lazy(() => import('./features/admin/BulkWalletAdjustPage'));
const CurriculumManager = lazy(() => import('./features/admin/curriculum/CurriculumManager').then(m => ({ default: m.CurriculumManager })));
const LevelDetail = lazy(() => import('./features/admin/curriculum/LevelDetail').then(m => ({ default: m.LevelDetail })));
const CurriculumAnchors = lazy(() => import('./features/admin/curriculum/CurriculumAnchors').then(m => ({ default: m.CurriculumAnchors })));
const SyllabusView = lazy(() => import('./features/curriculum/SyllabusView').then(m => ({ default: m.SyllabusView })));
const SupportInboxPage = lazy(() => import('./features/admin/SupportInboxPage').then(m => ({ default: m.SupportInboxPage })));
const ChurnDashboard = lazy(() => import('./features/admin/ChurnDashboard').then(m => ({ default: m.ChurnDashboard })));
const NotFoundPage = lazy(() => import('./features/NotFoundPage'));
const CompetitionsAdminPage = lazy(() => import('./features/gamification/CompetitionsAdminPage'));
const WebsiteLeadsPage = lazy(() => import('./features/admin/WebsiteLeadsPage'));
const VoiceCallsAdminPage = lazy(() => import('./features/voice/VoiceCallsAdminPage').then(m => ({ default: m.VoiceCallsAdminPage })));

// Support
const ContactSupportPage = lazy(() => import('./features/support/ContactSupportPage').then(m => ({ default: m.ContactSupportPage })));

// Coach features
const StudentList = lazy(() => import('./features/coaches/StudentList').then(m => ({ default: m.StudentList })));
const MyAttendance = lazy(() => import('./features/attendance/MyAttendance').then(m => ({ default: m.MyAttendance })));
const AttendanceSheet = lazy(() => import('./features/attendance/AttendanceSheet').then(m => ({ default: m.AttendanceSheet })));
const QuickCheckIn = lazy(() => import('./features/attendance/QuickCheckIn').then(m => ({ default: m.QuickCheckIn })));
const CoachAttendanceDashboard = lazy(() => import('./features/attendance/CoachAttendanceDashboard').then(m => ({ default: m.CoachAttendanceDashboard })));

// LMS features
const CourseList = lazy(() => import('./features/lms/courses/CourseList').then(m => ({ default: m.CourseList })));
const CourseDetail = lazy(() => import('./features/lms/courses/CourseDetail').then(m => ({ default: m.CourseDetail })));
const LessonViewer = lazy(() => import('./features/lms/viewer/LessonViewer').then(m => ({ default: m.LessonViewer })));

// Knowledgebase
const KbHome = lazy(() => import('./features/knowledgebase/KbHome').then(m => ({ default: m.KbHome })));
const CategoryView = lazy(() => import('./features/knowledgebase/CategoryView').then(m => ({ default: m.CategoryView })));
const ArticleView = lazy(() => import('./features/knowledgebase/ArticleView').then(m => ({ default: m.ArticleView })));
const KbAdminDashboard = lazy(() => import('./features/knowledgebase/admin/KbAdminDashboard').then(m => ({ default: m.KbAdminDashboard })));
const ArticleEditor = lazy(() => import('./features/knowledgebase/admin/ArticleEditor').then(m => ({ default: m.ArticleEditor })));

// Lazy load StudentFeedbackHistory
const StudentFeedbackHistory = lazy(() => import('./features/dashboard/StudentFeedbackHistory').then(m => ({ default: m.StudentFeedbackHistory })));

// Component to decide which attendance view to show
const AttendanceRoute = () => {
  const { currentUser } = useAuth();
  // Anyone who can mark OTHER members present gets the management tool; everyone
  // else gets their own history. This used to be `isAdmin || isCoach`, which sent
  // COORDINATORS to the student view even though the permissions matrix granted
  // them attendance_manage — the matrix said one thing and the app did another.
  //
  // canMarkAttendance() is the single capability helper (src/lib/permissions.ts)
  // and takes the union of ALL the user's roles, so student+coordinator resolves
  // to the staff surface: staff capability wins for surface selection.
  if (canMarkAttendance(currentUser)) {
    return <AttendancePage />;
  }
  return <MyAttendance />;
};

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <ActiveChildProvider>
            <BranchProvider>
              <RolesProvider>
                <GuidedTourProvider>
                  <Routes>
                    {/* Public routes - no layout */}
                    <Route path="/login" element={
                      <Suspense fallback={<LoadingScreen message="Loading..." />}>
                        <Login />
                      </Suspense>
                    } />
                    <Route path="/register" element={
                      <Suspense fallback={<LoadingScreen message="Loading..." />}>
                        <Register />
                      </Suspense>
                    } />
                    <Route path="/register/parent" element={
                      <Suspense fallback={<LoadingScreen message="Loading..." />}>
                        <ParentRegister />
                      </Suspense>
                    } />
                    {/* Public Routes with Layout */}
                    <Route element={<PublicLayout />}>
                      <Route path="/u/:username" element={
                        <Suspense fallback={<LoadingScreen message="Loading profile..." />}>
                          <PublicProfile />
                        </Suspense>
                      } />
                    </Route>

                    {/* 
                Protected routes with shared DashboardLayout (render once)
                All child routes render inside DashboardLayout via Outlet
              */}
                    <Route element={<ProtectedLayout />}>
                      <Route path="/dashboard" element={
                        <Suspense fallback={<LoadingScreen message="Loading dashboard..." />}>
                          <Dashboard />
                        </Suspense>
                      } />
                      <Route path="/feed" element={
                        <Suspense fallback={<LoadingScreen message="Loading feed..." />}>
                          <FeedPage />
                        </Suspense>
                      } />
                      <Route path="/messages" element={
                        <Suspense fallback={<LoadingScreen message="Loading messages..." />}>
                          <MessagesPage />
                        </Suspense>
                      } />
                      <Route path="/notifications" element={
                        <Suspense fallback={<LoadingScreen message="Loading notifications..." />}>
                          <NotificationsPage />
                        </Suspense>
                      } />
                      <Route path="/contact-support" element={
                        <Suspense fallback={<LoadingScreen message="Loading..." />}>
                          <ContactSupportPage />
                        </Suspense>
                      } />
                      <Route path="/points" element={
                        <Suspense fallback={<LoadingScreen message="Loading points..." />}>
                          <PointsHistoryPage />
                        </Suspense>
                      } />
                      <Route path="/schedule" element={
                        <Suspense fallback={<LoadingScreen message="Loading schedule..." />}>
                          <ClassSchedule />
                        </Suspense>
                      } />
                      <Route path="/leaderboard" element={
                        <Suspense fallback={<LoadingScreen message="Loading leaderboard..." />}>
                          <LeaderboardPage />
                        </Suspense>
                      } />
                      <Route path="/lms" element={
                        <Suspense fallback={<LoadingScreen message="Loading courses..." />}>
                          <CourseList />
                        </Suspense>
                      } />
                      <Route path="/lms/courses/:courseId" element={
                        <Suspense fallback={<LoadingScreen message="Loading course..." />}>
                          <CourseDetail />
                        </Suspense>
                      } />
                      <Route path="/lms/courses/:courseId/lessons/:lessonId" element={
                        <Suspense fallback={<LoadingScreen message="Loading lesson..." />}>
                          <LessonViewer />
                        </Suspense>
                      } />
                      <Route path="/profile" element={
                        <Suspense fallback={<LoadingScreen message="Loading profile..." />}>
                          <ProfilePage />
                        </Suspense>
                      } />
                      <Route path="/profile/:userId" element={
                        <Suspense fallback={<LoadingScreen message="Loading profile..." />}>
                          <ProfilePage />
                        </Suspense>
                      } />
                      <Route path="/wallet" element={
                        <Suspense fallback={<LoadingScreen message="Loading wallet..." />}>
                          <WalletPage />
                        </Suspense>
                      } />
                      <Route path="/family-wallet" element={
                        <Suspense fallback={<LoadingScreen message="Loading family wallet..." />}>
                          <FamilyWalletPage />
                        </Suspense>
                      } />
                      <Route path="/my-subscription" element={
                        <Suspense fallback={<LoadingScreen message="Loading subscription..." />}>
                          <MySubscriptionPage />
                        </Suspense>
                      } />
                      <Route path="/notes" element={
                        <div className="text-center py-12">
                          <h2 className="text-2xl font-bold text-gray-900">Notes</h2>
                          <p className="text-gray-600 mt-2">Coming soon</p>
                        </div>
                      } />

                      <Route path="/feedback-history/:uid?" element={
                        <FeedbackHistoryGuard>
                          <Suspense fallback={<LoadingScreen message="Loading history..." />}>
                            <StudentFeedbackHistory />
                          </Suspense>
                        </FeedbackHistoryGuard>
                      } />

                      {/* Unified Attendance Route */}
                      <Route path="/attendance" element={
                        <Suspense fallback={<LoadingScreen message="Loading attendance..." />}>
                          <AttendanceRoute />
                        </Suspense>
                      } />

                      {/* Knowledge Base (Protected Navigation) */}
                      <Route path="/kb" element={
                        <Suspense fallback={<LoadingScreen message="Loading knowledge base..." />}>
                          <KbHome />
                        </Suspense>
                      } />
                      <Route path="/kb/:categorySlug" element={
                        <Suspense fallback={<LoadingScreen message="Loading category..." />}>
                          <CategoryView />
                        </Suspense>
                      } />
                      <Route path="/kb/:categorySlug/:articleSlug" element={
                        <Suspense fallback={<LoadingScreen message="Loading article..." />}>
                          <ArticleView />
                        </Suspense>
                      } />

                      {/* Coach-only Routes */}
                      <Route path="/students" element={
                        <RoleGuard requiredRole="coach">
                          <Suspense fallback={<LoadingScreen message="Loading students..." />}>
                            <StudentList />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/attendance/dashboard" element={
                        <RoleGuard requiredRole="coach">
                          <Suspense fallback={<LoadingScreen message="Loading dashboard..." />}>
                            <CoachAttendanceDashboard />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/attendance/class/:classId" element={
                        <RoleGuard requiredRole="coach">
                          <Suspense fallback={<LoadingScreen message="Loading attendance..." />}>
                            <AttendanceSheet />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/attendance/checkin/:classId" element={
                        <RoleGuard requiredRole={['coach', 'super_admin']}>
                          <Suspense fallback={<LoadingScreen message="Loading check-in..." />}>
                            <QuickCheckIn />
                          </Suspense>
                        </RoleGuard>
                      } />

                      {/* Admin-only Routes */}
                      <Route path="/users" element={
                        <RoleGuard requiredPermission={{ module: 'users_view', action: 'read' }}>
                          <Suspense fallback={<LoadingScreen message="Loading users..." />}>
                            <UserManagement />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/users" element={
                        <RoleGuard requiredPermission={{ module: 'users_view', action: 'read' }}>
                          <Suspense fallback={<LoadingScreen message="Loading users..." />}>
                            <UserManagement />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/members/:uid" element={
                        <RoleGuard requiredPermission={{ module: 'users_view', action: 'read' }}>
                          <Suspense fallback={<LoadingScreen message="Loading member..." />}>
                            <MemberProfilePage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/audit" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading audit logs..." />}>
                            <AuditLogPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/wallet-bulk" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading bulk wallet adjust..." />}>
                            <BulkWalletAdjustPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/churn" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading churn metrics..." />}>
                            <ChurnDashboard />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/competitions" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading competitions..." />}>
                            <CompetitionsAdminPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/leads" element={
                        <RoleGuard requiredRole={['admin', 'super_admin']}>
                          <Suspense fallback={<LoadingScreen message="Loading website leads..." />}>
                            <WebsiteLeadsPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/voice-calls" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading voice calls..." />}>
                            <VoiceCallsAdminPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      {/* Member-facing syllabus. READ ONLY, and deliberately NOT under
                          /admin — any authenticated member may open it; the nav item is
                          what gates who is offered it, and the page's own empty state
                          covers anyone who arrives without content for their belt. */}
                      <Route path="/syllabus" element={
                        <Suspense fallback={<LoadingScreen message="Loading syllabus..." />}>
                          <SyllabusView />
                        </Suspense>
                      } />
                      <Route path="/admin/curriculum" element={
                        <RoleGuard requiredRole={['admin', 'super_admin']}>
                          <Suspense fallback={<LoadingScreen message="Loading curriculum..." />}>
                            <CurriculumManager />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/curriculum/anchors" element={
                        <RoleGuard requiredRole={['admin', 'super_admin']}>
                          <Suspense fallback={<LoadingScreen message="Loading anchors..." />}>
                            <CurriculumAnchors />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/curriculum/level/:levelId" element={
                        <RoleGuard requiredRole={['admin', 'super_admin']}>
                          <Suspense fallback={<LoadingScreen message="Loading curriculum..." />}>
                            <LevelDetail />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/support-inbox" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading support inbox..." />}>
                            <SupportInboxPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/kb" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading KB admin..." />}>
                            <KbAdminDashboard />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/kb/new" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading editor..." />}>
                            <ArticleEditor />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/admin/kb/edit/:articleId" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading editor..." />}>
                            <ArticleEditor />
                          </Suspense>
                        </RoleGuard>
                      } />

                      <Route path="/finance/*" element={
                        <RoleGuard requiredPermission={{ module: 'finance_view', action: 'read' }}>
                          <Suspense fallback={<LoadingScreen message="Loading finance..." />}>
                            <FinancePage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/settings" element={
                        <RoleGuard requiredPermission={{ module: 'settings_general', action: 'read' }}>
                          <Suspense fallback={<LoadingScreen message="Loading settings..." />}>
                            <SettingsPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/subscriptions" element={
                        <RoleGuard requiredPermission={{ module: 'subscriptions_view', action: 'read' }}>
                          <Suspense fallback={<LoadingScreen message="Loading subscriptions..." />}>
                            <SubscriptionsPage />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/performance" element={
                        <RoleGuard requiredRole="coach">
                          <Suspense fallback={<LoadingScreen message="Loading performance..." />}>
                            <PerformanceDashboard />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/performance/:studentId" element={
                        <RoleGuard requiredRole="coach">
                          <Suspense fallback={<LoadingScreen message="Loading performance..." />}>
                            <PerformanceDashboard />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/analytics" element={
                        <RoleGuard requiredRole="super_admin">
                          <Suspense fallback={<LoadingScreen message="Loading analytics..." />}>
                            <AnalyticsDashboard />
                          </Suspense>
                        </RoleGuard>
                      } />
                      <Route path="/onboarding" element={
                        <Suspense fallback={<LoadingScreen message="Setting up..." />}>
                          <OnboardingWizard />
                        </Suspense>
                      } />
                    </Route>

                    {/* Default redirect */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    {/* An unknown address says so — it no longer pretends to be the dashboard. */}
                    <Route path="*" element={
                      <Suspense fallback={<LoadingScreen message="Loading..." />}>
                        <NotFoundPage />
                      </Suspense>
                    } />
                  </Routes>
                </GuidedTourProvider>
              </RolesProvider>
            </BranchProvider>
          </ActiveChildProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
